import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Chip, TextField, Select, MenuItem, IconButton,
  ToggleButtonGroup, ToggleButton, Tabs, Tab, InputAdornment, Tooltip,
  CircularProgress, Button, Skeleton, Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import {
  getNewsFeed, getTrendingTickers, getNewsSources, getNewsStats,
  markArticleRead, triggerNewsScrape,
  getTwitterFollows, addTwitterFollow, removeTwitterFollow, toggleTwitterFollow,
  NewsArticleDB, NewsFeedResponse, TrendingTicker, NewsFilters, TwitterFollowItem,
} from '../services/api';
import { bloomberg } from '../theme/bloombergTheme';

// ── Source colors for visual scanning ──
const SOURCE_COLORS: Record<string, string> = {
  'CNBC': '#007ec6',
  'MarketWatch': '#00a651',
  'Reuters': '#ff8000',
  'Yahoo Finance': '#6001d2',
  'Seeking Alpha': '#f7971e',
  'BBC': '#bb1919',
  'Motley Fool': '#5b21b6',
  'Zacks': '#1e40af',
  'Google News': '#4285f4',
  'Investing.com': '#2c6e49',
  'Federal Reserve': '#003366',
  'IMF': '#003366',
  'SEC EDGAR': '#1e3a5f',
};
const REDDIT_COLOR = '#ff4500';
const TWITTER_COLOR = '#1DA1F2';

function getSourceColor(source: string): string {
  if (source.startsWith('Reddit:')) return REDDIT_COLOR;
  if (source.startsWith('Twitter:')) return TWITTER_COLOR;
  return SOURCE_COLORS[source] || bloomberg.accent.blue;
}

const CATEGORY_COLORS: Record<string, string> = {
  Earnings: '#00bcd4', Economy: '#ff9800', Markets: '#9c27b0', Technology: '#2196f3',
  'M&A': '#e91e63', Finance: '#4caf50', Healthcare: '#f44336', Energy: '#795548',
  Social: '#ff4500', Filing: '#1e3a5f', IPO: '#00e676', General: '#607d8b',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const CATEGORIES = ['Earnings', 'Economy', 'Markets', 'Technology', 'M&A', 'Finance', 'Healthcare', 'Energy', 'Social', 'Filing', 'General'];

const News = () => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState<NewsArticleDB[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sources, setSources] = useState<string[]>([]);
  const [trending, setTrending] = useState<TrendingTicker[]>([]);
  const [stats, setStats] = useState<{ total: number; unread: number; last_scrape: string | null } | null>(null);
  const [activeTab, setActiveTab] = useState(0); // 0=All, 1=News, 2=Social, 3=Filings
  const [scraping, setScraping] = useState(false);

  // Twitter follows
  const [twitterFollows, setTwitterFollows] = useState<TwitterFollowItem[]>([]);
  const [newHandle, setNewHandle] = useState('');
  const [addingFollow, setAddingFollow] = useState(false);

  // Filters
  const [filterSource, setFilterSource] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSentiment, setFilterSentiment] = useState('');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildFilters = useCallback((): NewsFilters => {
    const f: NewsFilters = { page, page_size: 30 };
    if (filterSource) f.source = filterSource;
    if (filterCategory) f.category = filterCategory;
    if (filterSentiment) f.sentiment = filterSentiment;
    if (filterSymbol) f.symbol = filterSymbol;
    if (filterSearch) f.search = filterSearch;
    if (activeTab === 1) f.content_type = 'news';
    if (activeTab === 2) f.content_type = 'social';
    if (activeTab === 3) f.content_type = 'filings';
    return f;
  }, [page, filterSource, filterCategory, filterSentiment, filterSymbol, filterSearch, activeTab]);

  const fetchArticles = useCallback(async (append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const data = await getNewsFeed(buildFilters());
      if (append) {
        setArticles(prev => [...prev, ...data.articles]);
      } else {
        setArticles(data.articles);
      }
      setTotal(data.total);
    } catch (e) {
      console.error('Error fetching news feed:', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [buildFilters]);

  // Initial load + sidebar data
  useEffect(() => {
    getNewsSources().then(setSources).catch(() => {});
    getTrendingTickers(24).then(setTrending).catch(() => {});
    getNewsStats().then(setStats).catch(() => {});
    getTwitterFollows().then(setTwitterFollows).catch(() => {});
  }, []);

  // Fetch articles when filters/tab/page change
  useEffect(() => {
    if (page === 1) fetchArticles(false);
    else fetchArticles(true);
  }, [page]); // eslint-disable-line

  // Reset page on filter/tab changes
  useEffect(() => {
    setPage(1);
    fetchArticles(false);
  }, [filterSource, filterCategory, filterSentiment, filterSymbol, activeTab]); // eslint-disable-line

  // Debounced search
  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      setPage(1);
      fetchArticles(false);
    }, 400);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [filterSearch]); // eslint-disable-line

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      if (page === 1) fetchArticles(false);
      getTrendingTickers(24).then(setTrending).catch(() => {});
      getNewsStats().then(setStats).catch(() => {});
    }, 300000);
    return () => clearInterval(interval);
  }, [page, fetchArticles]);

  const clearFilters = () => {
    setFilterSource(''); setFilterCategory(''); setFilterSentiment('');
    setFilterSymbol(''); setFilterSearch('');
  };
  const hasFilters = filterSource || filterCategory || filterSentiment || filterSymbol || filterSearch;

  const handleScrape = async () => {
    setScraping(true);
    try {
      await triggerNewsScrape();
      setPage(1);
      await fetchArticles(false);
      getNewsStats().then(setStats).catch(() => {});
      getTrendingTickers(24).then(setTrending).catch(() => {});
    } finally {
      setScraping(false);
    }
  };

  const handleAddFollow = async () => {
    const handle = newHandle.trim().replace(/^@/, '');
    if (!handle) return;
    setAddingFollow(true);
    try {
      const follow = await addTwitterFollow(handle);
      setTwitterFollows(prev => [follow, ...prev.filter(f => f.id !== follow.id)]);
      setNewHandle('');
    } catch { }
    setAddingFollow(false);
  };

  const handleRemoveFollow = async (id: number) => {
    try {
      await removeTwitterFollow(id);
      setTwitterFollows(prev => prev.filter(f => f.id !== id));
    } catch { }
  };

  const handleToggleFollow = async (id: number) => {
    try {
      const result = await toggleTwitterFollow(id);
      setTwitterFollows(prev => prev.map(f => f.id === result.id ? { ...f, is_active: result.is_active } : f));
    } catch { }
  };

  const handleArticleClick = (article: NewsArticleDB) => {
    if (!article.is_read) {
      markArticleRead(article.id).catch(() => {});
      setArticles(prev => prev.map(a => a.id === article.id ? { ...a, is_read: true } : a));
    }
    window.open(article.url, '_blank', 'noopener');
  };

  const SentimentIcon = ({ sentiment }: { sentiment?: string }) => {
    if (sentiment === 'positive') return <TrendingUpIcon sx={{ fontSize: 14, color: '#22c55e' }} />;
    if (sentiment === 'negative') return <TrendingDownIcon sx={{ fontSize: 14, color: '#ef4444' }} />;
    return <TrendingFlatIcon sx={{ fontSize: 14, color: bloomberg.text.muted }} />;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Intelligence Feed</Typography>
          {stats && (
            <Chip
              label={`${stats.total.toLocaleString()} articles`}
              size="small"
              sx={{ fontWeight: 600, fontSize: '0.72rem' }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {stats?.last_scrape && (
            <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>
              Last scan: {timeAgo(stats.last_scrape)}
            </Typography>
          )}
          <Tooltip title="Scrape now">
            <IconButton size="small" onClick={handleScrape} disabled={scraping}>
              {scraping ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filter bar */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }} elevation={1}>
        <TextField
          size="small" placeholder="Search headlines..."
          value={filterSearch}
          onChange={e => setFilterSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 200, flex: '1 1 200px', '& .MuiInputBase-root': { fontSize: 12 } }}
        />
        <Select
          size="small" displayEmpty value={filterSource}
          onChange={e => setFilterSource(e.target.value)}
          sx={{ minWidth: 140, fontSize: 12 }}
        >
          <MenuItem value="">All Sources</MenuItem>
          {sources.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 12 }}>{s}</MenuItem>)}
        </Select>
        <Select
          size="small" displayEmpty value={filterCategory}
          onChange={e => setFilterCategory(e.target.value)}
          sx={{ minWidth: 130, fontSize: 12 }}
        >
          <MenuItem value="">All Categories</MenuItem>
          {CATEGORIES.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
        </Select>
        <ToggleButtonGroup
          size="small" exclusive value={filterSentiment}
          onChange={(_, v) => setFilterSentiment(v || '')}
          sx={{ '& .MuiToggleButton-root': { py: 0.4, px: 1, fontSize: 11, textTransform: 'none' } }}
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="positive" sx={{ color: '#22c55e !important' }}>+</ToggleButton>
          <ToggleButton value="neutral">~</ToggleButton>
          <ToggleButton value="negative" sx={{ color: '#ef4444 !important' }}>−</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small" placeholder="Ticker..."
          value={filterSymbol}
          onChange={e => setFilterSymbol(e.target.value.toUpperCase())}
          sx={{ width: 90, '& .MuiInputBase-root': { fontSize: 12 } }}
        />
        {hasFilters && (
          <Tooltip title="Clear filters">
            <IconButton size="small" onClick={clearFilters}><ClearIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: `1px solid ${bloomberg.border.primary}`, mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="All" />
          <Tab label="News" />
          <Tab label="Social" />
          <Tab label="Filings" />
        </Tabs>
      </Box>

      {/* Main content + sidebar */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 260px' }, gap: 2 }}>

        {/* Article feed */}
        <Box>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rectangular" height={90} sx={{ borderRadius: 1 }} />)}
            </Box>
          ) : articles.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {hasFilters ? 'No articles match the current filters.' : 'No articles yet. Click the refresh button to trigger a news scrape.'}
              </Typography>
            </Paper>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {articles.map(article => {
                  const isNew = Date.now() - new Date(article.published_at).getTime() < 1800000;
                  return (
                    <Paper
                      key={article.id}
                      elevation={0}
                      onClick={() => handleArticleClick(article)}
                      sx={{
                        p: 1.5, cursor: 'pointer',
                        borderLeft: article.is_read ? 'none' : `3px solid ${bloomberg.accent.orange}`,
                        bgcolor: bloomberg.bg.secondary,
                        border: `1px solid ${bloomberg.border.primary}`,
                        borderRadius: 1,
                        '&:hover': { bgcolor: bloomberg.bg.hover, borderColor: bloomberg.border.secondary },
                        transition: 'background 0.15s',
                      }}
                    >
                      {/* Top row: source + time + badges */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={article.source}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.65rem', fontWeight: 700,
                            bgcolor: getSourceColor(article.source) + '22',
                            color: getSourceColor(article.source),
                            borderRadius: '4px',
                          }}
                        />
                        {isNew && (
                          <Chip label="NEW" size="small" sx={{
                            height: 18, fontSize: '0.6rem', fontWeight: 800,
                            bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444',
                            animation: 'pulse 2s infinite',
                          }} />
                        )}
                        <Typography variant="caption" sx={{ color: bloomberg.text.muted, ml: 'auto', fontSize: '0.68rem' }}>
                          {timeAgo(article.published_at)}
                        </Typography>
                      </Box>

                      {/* Title */}
                      <Typography sx={{
                        fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.35, mb: 0.5,
                        color: article.is_read ? bloomberg.text.secondary : bloomberg.text.primary,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}>
                        {article.title}
                      </Typography>

                      {/* Summary */}
                      {article.summary && (
                        <Typography sx={{
                          fontSize: '0.75rem', color: bloomberg.text.muted, lineHeight: 1.3, mb: 0.75,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                          {article.summary}
                        </Typography>
                      )}

                      {/* Bottom row: sentiment + category + tickers */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        {article.sentiment && (
                          <Chip
                            icon={<SentimentIcon sentiment={article.sentiment} />}
                            label={article.sentiment}
                            size="small"
                            sx={{ height: 20, fontSize: '0.62rem', fontWeight: 600 }}
                          />
                        )}
                        {article.category && (
                          <Chip
                            label={article.category}
                            size="small"
                            sx={{
                              height: 20, fontSize: '0.62rem', fontWeight: 600,
                              bgcolor: (CATEGORY_COLORS[article.category] || '#607d8b') + '22',
                              color: CATEGORY_COLORS[article.category] || '#607d8b',
                            }}
                          />
                        )}
                        {article.related_tickers?.slice(0, 3).map(t => (
                          <Chip
                            key={t} label={t} size="small"
                            onClick={e => { e.stopPropagation(); navigate(`/stock/${t}`); }}
                            sx={{
                              height: 20, fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer',
                              bgcolor: bloomberg.accent.blueDim, color: bloomberg.accent.blue,
                              '&:hover': { bgcolor: bloomberg.accent.blue, color: '#fff' },
                            }}
                          />
                        ))}
                        <OpenInNewIcon sx={{ fontSize: 12, color: bloomberg.text.muted, ml: 'auto' }} />
                      </Box>
                    </Paper>
                  );
                })}
              </Box>

              {/* Load more */}
              {articles.length < total && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="outlined" size="small"
                    onClick={() => setPage(p => p + 1)}
                    disabled={loadingMore}
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    {loadingMore ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    Load More — showing {articles.length} of {total.toLocaleString()}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Trending sidebar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper sx={{ p: 2 }} elevation={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Trending Tickers (24h)</Typography>
            {trending.length === 0 ? (
              <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>No data yet</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {trending.map((t, i) => (
                  <Box
                    key={t.symbol}
                    onClick={() => navigate(`/stock/${t.symbol}`)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 1, py: 0.5, borderRadius: 0.5, cursor: 'pointer',
                      '&:hover': { bgcolor: bloomberg.bg.hover },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: bloomberg.text.muted, width: 16 }}>{i + 1}</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: bloomberg.accent.orange }}>{t.symbol}</Typography>
                    </Box>
                    <Chip label={`${t.count}`} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }} />
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          {/* Stats */}
          {stats && (
            <Paper sx={{ p: 2 }} elevation={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Feed Stats</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>Total Articles</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>{stats.total.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>Unread</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: bloomberg.accent.orange }}>{stats.unread.toLocaleString()}</Typography>
                </Box>
                {stats.last_scrape && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ color: bloomberg.text.muted }}>Last Scrape</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{timeAgo(stats.last_scrape)}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          {/* Twitter / X Follows */}
          <Paper sx={{ p: 2 }} elevation={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              𝕏 Follows
              <Chip label={twitterFollows.filter(f => f.is_active).length} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />
            </Typography>
            <Typography variant="caption" sx={{ color: bloomberg.text.muted, display: 'block', mb: 1, fontSize: '0.65rem' }}>
              Scraped via Nitter RSS
            </Typography>

            {/* Add handle */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5 }}>
              <TextField
                size="small" placeholder="@handle"
                value={newHandle}
                onChange={e => setNewHandle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddFollow(); }}
                sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: 12, height: 32 } }}
              />
              <IconButton
                size="small" onClick={handleAddFollow} disabled={addingFollow || !newHandle.trim()}
                sx={{ border: `1px solid ${bloomberg.border.primary}`, borderRadius: 1, width: 32, height: 32 }}
              >
                {addingFollow ? <CircularProgress size={14} /> : <AddIcon fontSize="small" />}
              </IconButton>
            </Box>

            {/* Follows list */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, maxHeight: 260, overflowY: 'auto' }}>
              {twitterFollows.map(f => (
                <Box
                  key={f.id}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 0.5, py: 0.25, borderRadius: 0.5,
                    opacity: f.is_active ? 1 : 0.45,
                    '&:hover': { bgcolor: bloomberg.bg.hover },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                    <Switch
                      size="small"
                      checked={f.is_active}
                      onChange={() => handleToggleFollow(f.id)}
                      sx={{ '& .MuiSwitch-switchBase': { p: 0.5 }, '& .MuiSwitch-thumb': { width: 12, height: 12 }, width: 36, height: 20 }}
                    />
                    <Typography sx={{
                      fontSize: '0.72rem', fontWeight: 600,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      @{f.handle}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => handleRemoveFollow(f.id)} sx={{ p: 0.25 }}>
                    <DeleteIcon sx={{ fontSize: 14, color: bloomberg.text.muted }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default News;
